import classNames from "classnames"
import React, { useCallback } from "react"

import LayoutItem from "../Layouts/LayoutItem"

import Link from "@components/elements/Link"
import PillButton from "@components/elements/V2/Buttons/PillButton"
import ArrowIcon from "@components/elements/V2/Icons/ArrowIcon"
import Typography from "@components/elements/V2/Typography"
import {
  ACCELERATE_EXPERIMENT_EVENT_NAME,
  PLANS_EXPERIMENT_EVENT_NAME,
} from "@constants/segmentEvents"
import { Color } from "@constants/V2/color"
import { useScrollAnimateSelf } from "@hooks/V2/useScrollAnimate"
import { trackSegmentEvent } from "@utils/analytics"
import {
  getAccentColor,
  getBackgroundColorClass,
  getTextColorClass,
} from "@utils/V2/color"

type CTACardProps = {
  ctaLink: string
  ctaText: string
  onClick?: VoidFunction
  backgroundColor: Color
  mobileGridOrder?: number
} & Partial<Storyblok.CTATrackingEvent>

const CTACard = ({
  ctaLink,
  ctaText,
  onClick,
  trackingEvent,
  backgroundColor,
  trackingEventKey,
  trackingEventValue,
  mobileGridOrder,
}: CTACardProps) => {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLElement>, link?: string) => {
      if (trackingEvent) {
        // this is to support custom plans tracking event
        // TODO rework this to be more generic in future

        let event: Record<string, string> | undefined
        if (trackingEvent === ACCELERATE_EXPERIMENT_EVENT_NAME) {
          const urlParams = new URLSearchParams(window.location.search)
          const buuidValue = urlParams.get("buuid") || ""
          const vValue = urlParams.get("v") || ""
          const pValue = urlParams.get("p") || ""
          event = { ["buuid"]: buuidValue, ["v"]: vValue, ["p"]: pValue }
        } else if (trackingEvent === PLANS_EXPERIMENT_EVENT_NAME) {
          const urlParams = new URLSearchParams(window.location.search)
          const buuidValue = urlParams.get("buuid") || ""
          event = { ["buuid"]: buuidValue }
        } else {
          event =
            trackingEventKey && trackingEventValue
              ? { [trackingEventKey]: trackingEventValue }
              : undefined
        }

        trackSegmentEvent(trackingEvent, event)
      }

      if (onClick) {
        onClick()
      } else if (link && link.startsWith("#")) {
        e.preventDefault()
        const [scrollId, clickId] = link.includes("@")
          ? link.split("@")
          : [link, null]

        if (clickId) {
          document.getElementById(clickId)?.click()
        }

        if (scrollId !== "#") {
          document
            .querySelector(scrollId)
            ?.scrollIntoView({ behavior: "smooth" })
        }
      }
    },
    [trackingEvent, onClick, trackingEventKey, trackingEventValue]
  )

  const icon = (
    <ArrowIcon
      className={classNames(
        getTextColorClass(getAccentColor(backgroundColor)),
        "transition-all duration-300 ease-linear sm:h-40 sm:w-40 md:h-[58px] md:w-[58px]"
      )}
    />
  )
  const { animationRefCallback } = useScrollAnimateSelf(true)

  return (
    <>
      {/* Desktop */}
      <LayoutItem
        as={Link}
        ref={animationRefCallback}
        // Mobile grid order is not needed for desktop
        link={ctaLink}
        onClick={(e) => handleClick(e, ctaLink)}
        className={classNames(
          getBackgroundColorClass(backgroundColor),
          "group col-span-2 hidden min-h-[389px] flex-col justify-between rounded-20 p-40 sm:flex lg:col-span-4"
        )}
      >
        {icon}
        <Typography
          text={ctaText}
          size="lead-lg"
          font="grotesk"
          weight="medium"
          color={getAccentColor(backgroundColor)}
          className="transition-all duration-300 ease-linear"
        />
      </LayoutItem>

      {/* Mobile */}
      <LayoutItem
        as={PillButton}
        icon={icon}
        size="medium"
        style="solid"
        text={ctaText}
        linkUrl={ctaLink}
        fontSize="body-sm"
        color={backgroundColor}
        trackingEvent={trackingEvent}
        trackingEventKey={trackingEventKey}
        trackingEventValue={trackingEventValue}
        className={classNames(
          getBackgroundColorClass(backgroundColor),
          "col-span-full sm:hidden"
        )}
        mobileGridOrder={mobileGridOrder}
      />
    </>
  )
}

export default CTACard
